import { useEffect, useMemo, useRef, useState, useId } from "react";
import { ChevronDown } from "lucide-react";
import { useDashboardUI } from "../context/GlobalContext";
import modelService from "../services/modelService";

/**
 * Group models by provider
 */
export const byProvider = (list) =>
  list?.reduce((acc, m) => {
    const k = m.provider_name || "other";

    if (!acc[k]) {
      acc[k] = {
        provider_logo_url: m.provider_logo_url ?? {
          light_theme: "",
          dark_theme: "",
        },
        models: [],
      };
    }

    acc[k].models.push(m);
    return acc;
  }, {});

/**
 * ModelPicker Component
 */
export function ModelPicker({
  label,
  selected = null,
  onChange,
  placeholder = "Select model",
  isImageMode = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState("light");
  const wrapperRef = useRef(null);
  const buttonId = useId();
  const { llmModels, setLlmModels } = useDashboardUI();
  const groupedModels = useMemo(() => byProvider(llmModels), [llmModels]);

  /**
   * Detect dark mode from <html class="dark">
   */
  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setTheme(isDark ? "dark" : "light");
  }, []);

  /**
   * Filter models based on image mode
   */
  const filteredGroupedModels = useMemo(() => {
    const allModels = Object.values(groupedModels || {}).flatMap(
      (g) => g.models
    );

    const filtered = isImageMode
      ? allModels.filter((m) => m.is_image_model)
      : allModels.filter((m) => !m.is_image_model);

    return byProvider(filtered);
  }, [groupedModels, isImageMode]);

  /**
   * Close on outside click
   */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  /**
   * Close on Escape
   */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const fetchAvailableLlmModelList = async () => {
    try {
      const data = await modelService.getLlmModels();
      setLlmModels(data || []);
    } catch (error) {
      console.error("Error fetching model list:", error);
    }
  }

  useEffect(() => {
    if (llmModels.length === 0) {
      fetchAvailableLlmModelList();
    }
  }, []);

  const handleSelect = (model) => {
    if (onChange) onChange(model);
    setIsOpen(false);
  };

  const renderProviders = (groups) =>
    Object.entries(groups).map(
      ([provider, { provider_logo_url, models }], i, a) => (
        <div key={provider}>
          {/* Provider Header */}
          <div
            className="sticky top-0 z-10 px-4 py-2 text-xs font-bold
              bg-white/65 backdrop-blur-xl
              text-gray-600 flex items-center gap-2"
          >
            <img
              src={
                theme !== "dark"
                  ? provider_logo_url.light_theme
                  : provider_logo_url.dark_theme
              }
              alt={provider}
              className="h-4 w-4"
            />
            <span>{provider}</span>
          </div>

          {/* Models */}
          {models.map((model) => (
            <div
              key={model.model_id}
              onClick={() => handleSelect(model)}
              className={`cursor-pointer px-7 py-2 hover:bg-gray-100 text-[14px]
                ${model.model_id === selected?.model_id
                  ? "font-semibold"
                  : ""
                }`}
            >
              <div>{model.display_name}</div>

              {model.display_description && (
                <div className="text-xs text-gray-500">
                  {model.display_description}
                </div>
              )}
            </div>
          ))}

          {/* Divider */}
          {i !== a.length - 1 && (
            <hr className="my-1 mx-3 border-t border-gray-300" />
          )}
        </div>
      )
    );

  const hasAnyModels = Object.keys(filteredGroupedModels).length > 0;

  return (
    <div className="space-y-2">
      {/* Label */}
      <label
        htmlFor={buttonId}
        className=" text-sm font-medium"
      >
        {label}
      </label>

      <div ref={wrapperRef} className="relative">
        {/* Trigger Button */}
        <button
          id={buttonId}
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="w-full border rounded-xl border-slate-200 bg-white
                     px-[14px] py-[10px] pr-10 text-left text-sm
                     text-slate-800 shadow-sm"
        >
          <span
            className={
              selected ? "" : "text-slate-400"
            }
          >
            {selected ? selected.display_name : placeholder}
          </span>

          <ChevronDown
            className={`absolute right-3 top-1/2 -translate-y-1/2 opacity-60 transition
              ${isOpen ? "rotate-180" : ""}`}
            size={18}
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div
            className="absolute left-0 top-full mt-2 w-60 rounded-2xl shadow-xl
              ring-1 ring-black/10 z-20
              backdrop-blur-xl bg-white/95 overflow-hidden"
          >
            <div className="max-h-60 overflow-auto custom-scrollbar">
              <div className="py-1">
                {hasAnyModels ? (
                  renderProviders(filteredGroupedModels)
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No models available.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
